// The one table every Laporan report renders: middle size,
// content-sized columns with the first column pinned and a sideways scroll
// only on overflow (see ReportTableLayout in reportColumns.tsx), the
// PAGE_SIZE pager with page memory, and the sort hook wiring. Pages pass
// only what is theirs: rows, columns, row key, onRow, rowClassName, summary.
import { useLayoutEffect, useRef, useState } from "react";
import { ConfigProvider, Table, TablePaginationConfig, TableProps } from "antd";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { PAGE_SIZE } from "shared/constants";
import { textPx } from "shared/constants/zoom";
import { translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { ColumnKind } from "./columnCatalogue";
import {
  COMFORT_WIDTH,
  GRID_WIDTH,
  GridState,
  ReportColumn,
  ReportLayoutMode,
  ReportTableLayout,
  WRAPPING_KINDS,
  layoutReportColumns,
} from "./reportColumns";

type PageMemory =
  | string
  | { page?: number; handleTableChange: (pagination: TablePaginationConfig) => void };

export type ReportTableProps<Row extends object> = {
  rowKey: TableProps<Row>["rowKey"];
  columns: ReportColumn<Row>[];
  dataSource: Row[];
  loading?: boolean;
  // A storage key (the wrapper owns the page memory) or the page's own hook
  // result, for a page that resets the pager itself.
  pageMemory?: PageMemory;
  onSortChange?: (sorter: unknown) => void;
  /**
   * The server's own count for the period, passed only by a report whose
   * list the server caps. The pager then says how many rows exist and points
   * at the Excel, which is never capped. This replaced a sentence above each
   * table: a row count belongs beside the page numbers, where every product
   * puts it, not in prose over the data.
   */
  serverTotal?: { total?: number; noun: string };
  pagination?: false;
  // "content" (default) or "shares"; see ReportTableLayout.
  layout?: ReportTableLayout;
  size?: "small" | "middle";
  sticky?: boolean;
  scrollY?: number;
  // shares layout only: overrides the minimum width computed from the columns.
  minWidth?: number;
  rowClassName?: TableProps<Row>["rowClassName"];
  onRow?: TableProps<Row>["onRow"];
  summary?: TableProps<Row>["summary"];
  // Extra handler for pages that also react to filter changes; called after
  // the page memory and the sort hook.
  onChange?: TableProps<Row>["onChange"];
};

export const ReportTable = <Row extends object>(props: ReportTableProps<Row>) => {
  // Unconditional hook call; with no key it is a no-op (page undefined,
  // uncontrolled pager), which is exactly what a page-owned memory needs.
  const own = useRememberedTablePage(
    typeof props.pageMemory === "string" ? props.pageMemory : undefined,
  );
  const memory = typeof props.pageMemory === "object" ? props.pageMemory : own;
  const layout = props.layout ?? "content";
  const grid = useLayoutMode(
    layout === "content",
    props.columns.map((col) => col.reportKind),
    props.dataSource,
  );
  const { columns, scrollX } = layoutReportColumns(props.columns, layout, grid.state);
  return (
    // 16px either side of a middle-size cell (32px between columns, the
    // spacing the payroll draft and the admin's other tables use; AntD's
    // middle default of 8px packed the columns against each other). The
    // vertical padding stays at AntD's 12px (rows of 40px on screen): a
    // report is read down a column, month against month, and a taller row
    // (tried at 20px, 2026-10-16) put fewer rows inside one glance and made
    // that comparison harder. The small size (the session-detail modal)
    // keeps its own padding.
    <ConfigProvider theme={{ components: { Table: { cellPaddingInlineMD: 16 } } }}>
    <div ref={grid.ref} data-report-layout={layout === "content" ? grid.state.mode : "shares"}>
    <Table<Row>
      // Scopes the one pagination rule in index.css that pushes the count to
      // the far left while the page numbers stay right; the content layout's
      // class carries its nowrap and wrap-cap rules (the shares layout keeps
      // AntD's wrapping headers).
      className={layout === "content" ? "report-table report-table-content" : "report-table"}
      // Not sticky: AntD's sticky header is a second table that copies the
      // body's column widths, so a header word wider than its body column
      // ("Memberships Bought" over a column of "17") ran past the card while
      // the body did not, and the grid judgement below saw a phantom
      // overflow. One table lets the browser size each column with its
      // header included. Ten rows per page keep the header near anyway.
      sticky={props.sticky ?? false}
      // Explicit either way. content: "auto" so the browser sizes each column
      // to its header and widest cell (AntD would force "fixed" because of
      // the sticky header and the pinned column, and a fixed layout gives
      // width-less columns an equal split). shares: a numeric scroll.x
      // alone leaves AntD's layout on "auto" when there is no sticky header,
      // and percentage widths need "fixed".
      tableLayout={layout === "content" ? "auto" : "fixed"}
      rowKey={props.rowKey}
      showSorterTooltip={false}
      size={props.size ?? "middle"}
      loading={props.loading}
      columns={columns}
      dataSource={props.dataSource}
      // content: "max-content" makes the table as wide as its one-line
      // columns need and never narrower than its card, so columns are never
      // squashed and the scroll appears only past the window's width.
      scroll={{
        x: layout === "content" ? scrollX : props.minWidth ?? scrollX,
        ...(props.scrollY ? { y: props.scrollY } : {}),
      }}
      pagination={
        props.pagination === false
          ? false
          : {
              current: memory.page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              // `total` is AntD's own, i.e. after any client-side column
              // filter, so a name search narrows the count as it should. The
              // truncation tail compares against the LOADED rows instead:
              // the server capped the fetch, and filtering what arrived
              // cannot change that.
              showTotal: (total: number, range: [number, number]) => {
                const capped =
                  typeof props.serverTotal?.total === "number" &&
                  props.serverTotal.total > props.dataSource.length;
                // One sentence either way. When the server capped the
                // fetch, the loaded count and the period's real count are
                // tied together in words ("250 yang dimuat (473 pembelian
                // pada periode ini)"), never set side by side, which read
                // as two unrelated numbers.
                return (
                  <span style={{ fontSize: textPx(13), color: colors.ink3 }}>
                    {capped
                      ? translateWithVars("table.pager_range_capped", {
                          from: range[0],
                          to: range[1],
                          total,
                          server_total: props.serverTotal?.total ?? 0,
                          noun: props.serverTotal?.noun ?? "",
                        })
                      : translateWithVars("table.pager_range", {
                          from: range[0],
                          to: range[1],
                          total,
                        })}
                  </span>
                );
              },
            }
      }
      onChange={(pagination, filters, sorter, extra) => {
        memory.handleTableChange(pagination);
        props.onSortChange?.(sorter);
        props.onChange?.(pagination, filters, sorter, extra);
      }}
      rowClassName={props.rowClassName}
      onRow={props.onRow}
      summary={props.summary}
    />
    </div>
    </ConfigProvider>
  );
};

/**
 * Grid when it fits, content widths when those fit, names squeezed when
 * that is enough, comfortable floors when the table scrolls either way.
 *
 * Each judgement measures what the table would need on the per-kind grid
 * and at bare content widths (laying it out both ways for a moment, styles
 * restored before anything paints), then picks the first step that fits the
 * card: the grid; content widths; the wrapping columns (names and text)
 * held below their natural widths, in proportion to how far each stands
 * above its floor, so a long name wraps before the table scrolls (the rule
 * the PDF planner follows); and last the COMFORT_WIDTH floors on every
 * column, since the table scrolls anyway and squeezing it buys nothing. The
 * answer is a function of the window and the rows alone, so a resize or a
 * page turn in either direction lands on the same step. Judged synchronously
 * after layout (before paint), then again on resize, when the rows change
 * (the first render is often headers only, before the data arrives),
 * whenever the column count changes (a language switch remounts the tree,
 * so the header words are covered too), and on any change inside the table
 * (a page turn swaps the rows without touching this component's props).
 */
const useLayoutMode = (enabled: boolean, kinds: ColumnKind[], rows: unknown[]) => {
  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<ReportLayoutMode>("grid");
  const [wrapWidths, setWrapWidths] = useState<Record<number, number>>({});
  const [squeezeWidths, setSqueezeWidths] = useState<Record<number, number>>({});
  const columnCount = kinds.length;
  const kindsRef = useRef(kinds);
  kindsRef.current = kinds;

  useLayoutEffect(() => {
    if (!enabled || !ref.current) return;
    const host = ref.current;
    const tables = () =>
      Array.from(
        host.querySelectorAll<HTMLElement>(
          ".ant-table-header table, .ant-table-body table, .ant-table-content table",
        ),
      );
    const judge = () => {
      const body = host.querySelector<HTMLElement>(".ant-table-body, .ant-table-content");
      const table = body?.querySelector<HTMLTableElement>("table");
      const ths = Array.from(host.querySelectorAll<HTMLTableCellElement>("thead th"));
      if (!body || !table || ths.length === 0) return;
      const room = body.clientWidth;
      const current = kindsRef.current;

      // The natural width of each wrapping column: its widest cell's content
      // on one line, capped at the column's wrap cap, plus the cell's own
      // padding. Measured on a hidden one-line clone of the content rather
      // than on the content itself, because under the auto layout a column
      // width acts as a cap for wrapping content: a cell laid out at the grid
      // width reads back that width (its chip dropped under the name), and
      // on a Flex it reads back the whole cell. offsetWidth is in layout px,
      // unaffected by the body zoom.
      // Alongside it, the narrowest the column can be laid out at: its
      // longest unbreakable word (a second clone at min-content width). The
      // squeeze below can hold a column no narrower than that, whatever its
      // floor says, or the table would overflow with the names stacked high.
      // And, for a cell that carries a chip beside or under its name (two or
      // more elements), the width its name needs on one line: the squeeze
      // may drop the chip under the name but never wrap the name as well,
      // so a squeezed row is two lines at most, never three.
      const measured: Record<number, number> = {};
      const narrowest: Record<number, number> = {};
      const nameFloor: Record<number, number> = {};
      const probes: { td: HTMLTableCellElement; wide: HTMLElement; narrow: HTMLElement }[] = [];
      body.querySelectorAll<HTMLTableCellElement>("td.report-cell-wrap").forEach((td) => {
        const inner = td.firstElementChild as HTMLElement | null;
        if (!inner) return;
        const hidden = ";position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;max-width:none;";
        const wide = inner.cloneNode(true) as HTMLElement;
        wide.style.cssText += hidden + "display:inline-flex;flex-wrap:nowrap;white-space:nowrap;width:max-content";
        const narrow = inner.cloneNode(true) as HTMLElement;
        // overflow-wrap is normal on the probe: the live cell may break a
        // word that is wider than the cap as a last resort, but the squeeze
        // must never cut a column below its longest whole word.
        narrow.style.cssText +=
          hidden + "display:inline-flex;flex-wrap:wrap;white-space:normal;overflow-wrap:normal;width:min-content";
        td.appendChild(wide);
        td.appendChild(narrow);
        probes.push({ td, wide, narrow });
      });
      probes.forEach(({ td, wide, narrow }) => {
        const style = getComputedStyle(td);
        const cap = parseFloat(style.getPropertyValue("--report-wrap-max")) || Infinity;
        const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        const w = Math.ceil(Math.min(wide.offsetWidth, cap) + pad) + 1;
        if (w > (measured[td.cellIndex] ?? 0)) measured[td.cellIndex] = w;
        const n = Math.ceil(Math.min(narrow.offsetWidth, cap) + pad) + 1;
        if (n > (narrowest[td.cellIndex] ?? 0)) narrowest[td.cellIndex] = n;
        const first = wide.firstElementChild as HTMLElement | null;
        if (first && wide.childElementCount > 1) {
          const f = Math.ceil(Math.min(first.offsetWidth, cap) + pad) + 1;
          if (f > (nameFloor[td.cellIndex] ?? 0)) nameFloor[td.cellIndex] = f;
        }
      });
      probes.forEach(({ wide, narrow }) => {
        wide.remove();
        narrow.remove();
      });
      const natural = (i: number) => measured[i] ?? 0;
      const wraps = (i: number) => WRAPPING_KINDS.includes(current[i]);

      // What the table needs at a set of column widths (undefined = the
      // column's own content): laid out for a moment as a max-content table
      // with the header cells carrying the widths (rc-table renders a <col>
      // only for columns that have a width, so the cells carry them here),
      // every style put back before this function returns.
      const cols = Array.from(table.querySelectorAll<HTMLTableColElement>("col"));
      const saved = {
        table: table.style.cssText,
        cols: cols.map((c) => c.style.width),
        ths: ths.map((th) => th.style.width),
      };
      const needAt = (widths: (number | undefined)[]) => {
        cols.forEach((c) => {
          c.style.width = "";
        });
        ths.forEach((th, i) => {
          th.style.width = widths[i] === undefined ? "" : `${widths[i]}px`;
        });
        table.style.minWidth = "0";
        table.style.width = "max-content";
        return table.offsetWidth;
      };
      const gridNeed = needAt(
        current.map((kind, i) => (wraps(i) ? Math.max(GRID_WIDTH[kind], natural(i)) : GRID_WIDTH[kind])),
      );
      const contentNeed = needAt(current.map(() => undefined));
      cols.forEach((c, i) => {
        c.style.width = saved.cols[i];
      });
      ths.forEach((th, i) => {
        th.style.width = saved.ths[i];
      });
      table.style.cssText = saved.table;

      // The squeeze: the wrapping columns give up `deficit` px, the column
      // with the most slack above its floor (or above its longest word, when
      // that is wider than the floor, or above the one-line width of a name
      // that carries a chip) first, the next only when it must.
      // Splitting a small deficit across every column in proportion wrapped
      // every name in a column of same-length names for a 22px cut while a
      // neighbour had 100px to spare; taking it from the slackest column
      // disturbs the fewest rows. Null when even the floors cannot absorb it.
      const squeeze = (deficit: number): Record<number, number> | null => {
        const idx = current.map((_, i) => i).filter(wraps);
        const give = new Map(
          idx.map((i) => [
            i,
            Math.max(
              0,
              natural(i) - Math.max(COMFORT_WIDTH[current[i]] ?? 0, narrowest[i] ?? 0, nameFloor[i] ?? 0),
            ),
          ]),
        );
        const giveTotal = Array.from(give.values()).reduce((sum, g) => sum + g, 0);
        if (giveTotal < deficit) return null;
        const widths: Record<number, number> = {};
        let left = deficit;
        idx
          .slice()
          .sort((a, b) => (give.get(b) ?? 0) - (give.get(a) ?? 0))
          .forEach((i) => {
            const cut = Math.min(give.get(i) ?? 0, left);
            left -= cut;
            widths[i] = Math.floor(natural(i) - cut);
          });
        return widths;
      };

      // offsetWidth rounds a fractional width to the nearest pixel, so a step
      // is taken only with a pixel to spare, and the squeeze gives up two
      // more than the rounded deficit (and floors its widths): a table that
      // "fits" by a rounded pixel would otherwise show a one-pixel scroll.
      let next: ReportLayoutMode;
      let squeezed: Record<number, number> = {};
      if (gridNeed <= room - 1) next = "grid";
      else if (contentNeed <= room - 1) next = "content";
      else {
        const widths = squeeze(contentNeed - room + 2);
        if (widths) {
          next = "squeeze";
          squeezed = widths;
        } else next = "comfort";
      }
      setWrapWidths((prev) => (sameWidths(prev, measured) ? prev : measured));
      setSqueezeWidths((prev) => (sameWidths(prev, squeezed) ? prev : squeezed));
      setMode(next);
    };
    judge();
    // An overflow inside a scroll container does not change the wrapper's
    // own size, so the observer watches the table too.
    const observer = new ResizeObserver(judge);
    observer.observe(host);
    tables().forEach((t) => observer.observe(t));
    // A page turn swaps the rows without a resize when the rows are all one
    // line, so any change inside the table is judged as well. The judgement's
    // own probes are mutations too; their records are discarded.
    const mutations = new MutationObserver(() => {
      judge();
      mutations.takeRecords();
    });
    mutations.observe(host, { childList: true, characterData: true, subtree: true });
    mutations.takeRecords();
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [enabled, mode, columnCount, rows]);

  const state: GridState = { mode: enabled ? mode : "content", wrapWidths, squeezeWidths };
  return { ref, state };
};

// Two width maps with the same columns and the same widths, to within half a
// pixel (squeezed widths are fractional and re-derived from integer layout
// measurements, so an exact comparison would re-render for nothing).
const sameWidths = (a: Record<number, number>, b: Record<number, number>): boolean => {
  const keys = Object.keys(b);
  return (
    keys.length === Object.keys(a).length && keys.every((k) => Math.abs((a[Number(k)] ?? NaN) - b[Number(k)]) < 0.5)
  );
};
